import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { fetchHistoryCandles } from "../services/marketData";
import {
  API_BASE_URL,
  getSessionToken,
  requestRadarJson,
} from "../services/radar/radarApi";
import {
  buildRadarTargets,
  RADAR_ALERT_DEDUPLICATION_MS,
  RADAR_DEFAULT_SL_PIPS,
  RADAR_HISTORY_CACHE_MS,
  RADAR_HISTORY_TIMEFRAMES,
  RADAR_LOG_LIMIT,
  RADAR_MAX_CONCURRENT_SCANS,
  RADAR_PRIORITY_ORDER,
  RADAR_SCAN_INTERVALS_MS,
  RADAR_TICK_MS,
} from "../services/radar/radarConfig";
import { runRadarPrecheck } from "../services/radar/radarPrecheck";
import { rankRadarCandidate } from "../services/radar/radarRanking";
import { runMasterPipeline } from "../utils/engine/masterPipeline";
import { evaluateZoneOutcome, withZoneOutcomeAnalysis } from "../utils/engine/zoneOutcomeTracker";
import soundManager from "../services/SoundManager";
import { deliverZoneAlert } from "../services/zoneAlertDelivery";

function createInitialState() {
  return {
    candidates: [],
    rejected: [],
    cooldowns: [],
    upcoming: [],
    latestScores: {},
    alerts: [],
    logs: [],
    lastScanTime: null,
    scanning: false,
    socketConnected: false,
    activeScanCount: 0,
    paused: false,
  };
}

function sortCandidates(candidates, sortBy, sortDirection) {
  const directionMultiplier = sortDirection === "asc" ? 1 : -1;

  return [...candidates].sort((left, right) => {
    if (sortBy === "symbol") {
      return left.symbol.localeCompare(right.symbol) * directionMultiplier;
    }

    if (sortBy === "timeframe") {
      return (left.timeframe - right.timeframe) * directionMultiplier;
    }

    if (sortBy === "confidence") {
      return (left.confidence - right.confidence) * directionMultiplier;
    }

    if (sortBy === "priority") {
      return (
        (RADAR_PRIORITY_ORDER[left.priority] - RADAR_PRIORITY_ORDER[right.priority]) *
        directionMultiplier
      );
    }

    return (left.score - right.score) * directionMultiplier;
  });
}

export default function useRadarScanner({
  user,
  enabled = true,
  sortBy = "score",
  sortDirection = "desc",
}) {
  const [state, setState] = useState(createInitialState);
  const registryRef = useRef(buildRadarTargets());
  const historyCacheRef = useRef(new Map());
  const candidateMapRef = useRef(new Map());
  const rejectedMapRef = useRef(new Map());
  const cooldownMapRef = useRef(new Map());
  const scoreMapRef = useRef(new Map());
  const alertsRef = useRef([]);
  const alertDedupRef = useRef(new Map());
  const inFlightRef = useRef(new Set());
  const timerRef = useRef(null);
  const mountedRef = useRef(false);
  const pausedRef = useRef(false);
  const zoneEventIdsRef = useRef(new Map());

  const pushLog = useCallback((message, extra = {}) => {
    const nextEntry = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      time: new Date().toLocaleTimeString(),
      message,
      extra,
    };

    setState((current) => ({
      ...current,
      logs: [nextEntry, ...current.logs].slice(0, RADAR_LOG_LIMIT),
    }));
  }, []);

  const syncCollections = useCallback(() => {
    const candidates = sortCandidates(
      [...candidateMapRef.current.values()],
      sortBy,
      sortDirection
    );
    const rejected = [...rejectedMapRef.current.values()]
      .sort((left, right) => right.lastUpdateTime - left.lastUpdateTime)
      .slice(0, 18);
    const cooldowns = [...cooldownMapRef.current.values()].sort(
      (left, right) => left.until - right.until
    );
    const upcoming = [...registryRef.current]
      .sort((left, right) => left.nextScanAt - right.nextScanAt)
      .slice(0, 8)
      .map((target) => ({
        id: target.id,
        symbol: target.symbol,
        timeframe: target.timeframe,
        timeframeLabel: target.timeframeLabel,
        market: target.market,
        hotness: target.hotness,
        nextScanAt: target.nextScanAt,
        queued: inFlightRef.current.has(target.id),
      }));

    setState((current) => ({
      ...current,
      candidates,
      rejected,
      cooldowns,
      upcoming,
      latestScores: Object.fromEntries(scoreMapRef.current.entries()),
      alerts: alertsRef.current.slice(0, 18),
      lastScanTime: Date.now(),
      activeScanCount: inFlightRef.current.size,
      scanning: inFlightRef.current.size > 0,
      paused: pausedRef.current,
    }));
  }, [sortBy, sortDirection]);

  const loadHistoryBundle = useCallback(async (symbol) => {
    const bundle = {};

    await Promise.all(
      RADAR_HISTORY_TIMEFRAMES.map(async (timeframe) => {
        const cacheKey = `${symbol}:${timeframe}`;
        const cached = historyCacheRef.current.get(cacheKey);

        if (cached && Date.now() - cached.cachedAt < RADAR_HISTORY_CACHE_MS) {
          bundle[timeframe] = cached.candles;
          return;
        }

        const candles = await fetchHistoryCandles({
          symbol,
          timeframe,
          includeStructure: false,
        });

        historyCacheRef.current.set(cacheKey, {
          candles,
          cachedAt: Date.now(),
        });
        bundle[timeframe] = candles;
      })
    );

    return bundle;
  }, []);

  const emitRadarAlert = useCallback(async (candidate) => {
    const dedupeKey = `${candidate.id}:${candidate.priority}:${candidate.readiness}`;
    const lastEmittedAt = alertDedupRef.current.get(dedupeKey);

    if (lastEmittedAt && Date.now() - lastEmittedAt < RADAR_ALERT_DEDUPLICATION_MS) {
      return;
    }

    alertDedupRef.current.set(dedupeKey, Date.now());

    const message = `${candidate.symbol} ${candidate.timeframeLabel} ${candidate.priority.replace(
      "_",
      " "
    )} ${candidate.direction} setup`;

    try {
      await requestRadarJson("/api/alerts/radar", {
        method: "POST",
        body: JSON.stringify({
          symbol: candidate.symbol,
          timeframe: candidate.timeframe,
          priority: candidate.priority,
          direction: candidate.direction,
          confidence: candidate.confidence,
          score: candidate.score,
          state: candidate.readiness,
          message,
          analysis: candidate.pipelineAnalysis,
        }),
      });
      pushLog("Radar alert emitted", {
        symbol: candidate.symbol,
        timeframe: candidate.timeframe,
        priority: candidate.priority,
      });
    } catch (error) {
      pushLog("Radar alert emit failed", {
        message: error.message,
        symbol: candidate.symbol,
      });
    }
  }, [pushLog]);

  const applyRejectedTarget = useCallback((target, reason, hotness = "idle") => {
    candidateMapRef.current.delete(target.id);
    scoreMapRef.current.set(target.id, 0);
    rejectedMapRef.current.set(target.id, {
      id: target.id,
      symbol: target.symbol,
      timeframe: target.timeframe,
      market: target.market,
      reason,
      hotness,
      lastUpdateTime: Date.now(),
    });
  }, []);

  const scanTarget = useCallback(async (target) => {
    inFlightRef.current.add(target.id);
    syncCollections();

    try {
      const histories = await loadHistoryBundle(target.symbol);
      const executionCandles = histories[target.timeframe];
      const candlesM5 = histories[300];
      const candlesM15 = histories[900];
      const candlesM30 = histories[1800];
      const precheck = runRadarPrecheck({
        market: target.market,
        executionCandles,
        candlesM5,
        candlesM15,
        candlesM30,
      });

      const previousCandidate = candidateMapRef.current.get(target.id) || null;
      const analysis = runMasterPipeline({
        candlesM1: executionCandles,
        candlesM5,
        candlesM15,
        candlesM30,
        market: target.market,
        symbol: target.symbol,
        timeframe: target.timeframe,
        prevResult: previousCandidate?.pipelineAnalysis?.result || null,
        slPips: RADAR_DEFAULT_SL_PIPS,
      });

      if (!analysis) {
        applyRejectedTarget(target, "Pipeline returned no analysis", "idle");
        target.nextScanAt = Date.now() + RADAR_SCAN_INTERVALS_MS.idle;
        return;
      }

      const zoneOutcome = evaluateZoneOutcome({
        symbol: target.symbol,
        timeframe: target.timeframe,
        market: target.market,
        candles: executionCandles,
        analysis,
      });
      const analysisWithOutcome = withZoneOutcomeAnalysis(analysis, zoneOutcome);

      const lastEvent = zoneOutcome?.lastEvent;
      if (lastEvent?.id && zoneEventIdsRef.current.get(target.id) !== lastEvent.id) {
        zoneEventIdsRef.current.set(target.id, lastEvent.id);
        deliverZoneAlert({
          eventType: lastEvent.eventType,
          symbol: target.symbol,
          timeframeLabel: target.timeframeLabel,
          zoneType: lastEvent.zone?.type,
          price: lastEvent.price,
        });
        pushLog(`${target.symbol} ${target.timeframeLabel} ${lastEvent.message || lastEvent.eventType}`, {
          symbol: target.symbol,
          timeframe: target.timeframe,
          eventType: lastEvent.eventType,
        });
      }

      const previousFailures = rejectedMapRef.current.has(target.id) ? 1 : 0;
      const candidate = rankRadarCandidate({
        symbol: target.symbol,
        timeframe: target.timeframe,
        market: target.market,
        analysis: analysisWithOutcome,
        precheck,
        previousCandidate,
        failureCount: previousFailures,
      });

      candidateMapRef.current.set(target.id, candidate);
      rejectedMapRef.current.delete(target.id);
      scoreMapRef.current.set(target.id, candidate.score);

      const nextInterval =
        candidate.priority === "HIGH_PRIORITY"
          ? RADAR_SCAN_INTERVALS_MS.hot
          : candidate.priority === "MEDIUM_PRIORITY"
          ? RADAR_SCAN_INTERVALS_MS.warm
          : RADAR_SCAN_INTERVALS_MS.idle;

      target.hotness = precheck.hotness;
      target.nextScanAt = Date.now() + nextInterval;

      if (candidate.priority === "HIGH_PRIORITY") {
        cooldownMapRef.current.set(target.id, {
          id: target.id,
          symbol: target.symbol,
          timeframe: target.timeframe,
          until: Date.now() + RADAR_SCAN_INTERVALS_MS.cooldown,
        });
        await emitRadarAlert(candidate);
      } else {
        cooldownMapRef.current.delete(target.id);
      }
    } catch (error) {
      applyRejectedTarget(target, error.message || "Scan failed", "idle");
      target.nextScanAt = Date.now() + RADAR_SCAN_INTERVALS_MS.cooldown;
      pushLog("Radar scan failed", {
        message: error.message,
        symbol: target.symbol,
        timeframe: target.timeframe,
      });
    } finally {
      inFlightRef.current.delete(target.id);
      syncCollections();
    }
  }, [applyRejectedTarget, emitRadarAlert, loadHistoryBundle, pushLog, syncCollections]);

  useEffect(() => {
    soundManager.initialize();

    return () => {
      soundManager.destroy();
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    syncCollections();
  }, [syncCollections]);

  useEffect(() => {
    if (!enabled || !user) {
      setState((current) => ({
        ...current,
        scanning: false,
      }));
      return undefined;
    }

    async function tick() {
      if (
        !mountedRef.current ||
        pausedRef.current ||
        inFlightRef.current.size >= RADAR_MAX_CONCURRENT_SCANS
      ) {
        return;
      }

      const now = Date.now();
      const dueTargets = registryRef.current
        .filter((target) => target.nextScanAt <= now && !inFlightRef.current.has(target.id))
        .slice(0, RADAR_MAX_CONCURRENT_SCANS - inFlightRef.current.size);

      if (!dueTargets.length) {
        return;
      }

      await Promise.all(dueTargets.map((target) => scanTarget(target)));
    }

    tick();
    timerRef.current = window.setInterval(tick, RADAR_TICK_MS);

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, scanTarget, sortBy, sortDirection, user]);

  useEffect(() => {
    if (!enabled || !user) {
      return undefined;
    }

    const token = getSessionToken();

    if (!token) {
      return undefined;
    }

    const socket = io(API_BASE_URL, {
      transports: ["polling", "websocket"],
      upgrade: true,
      reconnection: true,
    });

    socket.on("connect", () => {
      setState((current) => ({
        ...current,
        socketConnected: true,
      }));
      socket.emit("alerts:subscribe-user", { token });
    });

    socket.on("disconnect", () => {
      setState((current) => ({
        ...current,
        socketConnected: false,
      }));
    });

    socket.on("radar_candidate", (payload) => {
      const candidateId = `${payload.symbol}:${Number(payload.timeframe)}`;
      const currentCandidate = candidateMapRef.current.get(candidateId) || {};

      // Replace the local candidate with the latest backend-confirmed confidence payload.
      candidateMapRef.current.set(candidateId, {
        ...currentCandidate,
        ...payload,
        id: candidateId,
        timeframe: Number(payload.timeframe),
        pipelineAnalysis: payload.analysis || currentCandidate.pipelineAnalysis || null,
        lastUpdateTime: Date.now(),
        lastUpdateLabel: new Date().toLocaleTimeString(),
      });
      scoreMapRef.current.set(candidateId, Number(payload.score) || 0);
      alertsRef.current = [
        payload,
        ...alertsRef.current.filter((alert) => alert.id !== payload.id),
      ].slice(0, 18);
      syncCollections();
    });

    return () => {
      socket.emit("alerts:unsubscribe-user", { token });
      socket.disconnect();
    };
  }, [enabled, syncCollections, user]);

  const metrics = useMemo(() => {
    const highPriorityCount = state.candidates.filter(
      (candidate) => candidate.priority === "HIGH_PRIORITY"
    ).length;
    const readyCount = state.candidates.filter(
      (candidate) => candidate.readiness === "READY"
    ).length;
    const monitoringCount = state.candidates.filter(
      (candidate) => candidate.readiness === "MONITORING"
    ).length;
    const averageScore = state.candidates.length
      ? Math.round(
          state.candidates.reduce((sum, candidate) => sum + candidate.score, 0) /
            state.candidates.length
        )
      : 0;
    const hotQueueCount = state.upcoming.filter((target) => target.hotness === "hot").length;
    const warmQueueCount = state.upcoming.filter((target) => target.hotness === "warm").length;
    const nextScanTarget = state.upcoming[0] || null;

    return {
      totalCandidates: state.candidates.length,
      highPriorityCount,
      readyCount,
      monitoringCount,
      averageScore,
      rejectedCount: state.rejected.length,
      socketConnected: state.socketConnected,
      lastScanTime: state.lastScanTime,
      activeScanCount: state.activeScanCount,
      cooldownCount: state.cooldowns.length,
      hotQueueCount,
      warmQueueCount,
      nextScanTarget,
      paused: state.paused,
    };
  }, [state]);

  return {
    ...state,
    metrics,
    refreshNow: () => {
      registryRef.current = registryRef.current.map((target) => ({
        ...target,
        nextScanAt: 0,
      }));
      pushLog("Manual radar refresh queued");
      syncCollections();
    },
    pauseScanning: () => {
      pausedRef.current = true;
      pushLog("Radar orchestration paused");
      syncCollections();
    },
    resumeScanning: () => {
      pausedRef.current = false;
      pushLog("Radar orchestration resumed");
      syncCollections();
    },
  };
}
